import { IStyle, mergeStyleSets } from "@fluentui/merge-styles";
import { mergeStyles, Stack } from "@fluentui/react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IconButton, Tooltip } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { IPartnerTenantDetailsProps } from "../../interfaces/PartnerView.interfaces";
import { COPY_TENANT_ID, DATA_GRID_CONFIG } from "../../SharedComponents/Constants/CustomCustomerList.constants";
import { CustomDataGrid } from "../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { CustomLinkNavigate } from "../../SharedComponents/CustomLinkNavigate";
import { CUSTOM_CUSTOMER_LIST_STYLES } from "../../SharedComponents/Styles/CustomCustomerList.styles";
import { copy, handleToggleDataGridRowSelection } from "../../Utils/js-common";

const styles = mergeStyleSets({
  featureTable: {
    height: 600,
    width: "100%"
  } as IStyle
});

export default function PartnerTenantDetailsTable(props: IPartnerTenantDetailsProps) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [title, setTitle] = useState(COPY_TENANT_ID);
  const [paginationOptions, setPaginationOptions] = useState(DATA_GRID_CONFIG.pagination);
  const handleSelection = handleToggleDataGridRowSelection(selectionModel, setSelectionModel);
  useEffect(() => {
    setTimeout(() => {
      const elements = document.getElementsByClassName("MuiDataGrid-virtualScroller");
      Array.from(elements).forEach(function (element) {
        element.setAttribute("tabIndex", "0");
      });
    }, 1000);
  }, []);

  const columns: GridColDef[] = [
    {
      headerName: "PartnerID",
      field: "mpnId",
      minWidth: 100,
      sortable: true,
      flex: 0.2
    },
    {
      headerName: "PartnerName",
      field: "mpnName",
      minWidth: 200,
      sortable: true,
      flex: 0.2
    },
    {
      headerName: "PartnerOneID",
      field: "partnerOneId",
      minWidth: 120,
      sortable: true,
      flex: 0.2
    },
    {
      headerName: "PartnerAssociationType",
      field: "partnerAssociationType",
      minWidth: 200,
      sortable: true,
      flex: 0.2
    },
    {
      headerName: "TenantId",
      field: "tenantId",
      minWidth: 200,
      sortable: true,
      flex: 1.2,
      renderCell: ({ row }: any) => (
        <Stack horizontal styles={{ root: CUSTOM_CUSTOMER_LIST_STYLES.customDataGridColumns.tenantIdContainer }}>
          <Tooltip title={title} leaveDelay={500} arrow>
            <IconButton
              size="small"
              onClick={() => copyValue(row.tenantId, setTitle)}
              onMouseEnter={() => onMouseEnterTenantId(setTitle)}
              className={mergeStyles(CUSTOM_CUSTOMER_LIST_STYLES.customDataGridColumns.tenantIdTooltipContainerBtn)}
            >
              <ContentCopyIcon sx={CUSTOM_CUSTOMER_LIST_STYLES.customDataGridColumns.tenantIdTooltipCopyIconBtn} />
            </IconButton>
          </Tooltip>
          <CustomLinkNavigate to={navigateTo(row.tenantId)} text={row.tenantId} />
        </Stack>
      )
    },
    {
      headerName: "Tenant Name",
      field: "tenantName",
      width: 210,
      sortable: true,
      flex: 1,
      renderCell: ({ row }: any) => <CustomLinkNavigate to={navigateTo(row.tenantId)} text={row.tenantName} />
    },
    {
      headerName: "SegmentGroup",
      field: "segmentGroup",
      width: 150,
      sortable: true,
      flex: 1
    },
    {
      headerName: "SubSegmentName",
      field: "subSegment",
      width: 250,
      sortable: true,
      flex: 1
    },
    {
      headerName: "IndustryName",
      field: "industryName",
      width: 150,
      sortable: true,
      flex: 1
    },
    {
      headerName: "VerticalName",
      field: "verticalName",
      width: 150,
      sortable: true,
      flex: 1
    },
    {
      headerName: "LoginMAU",
      field: "loginMAU",
      width: 150,
      sortable: true,
      type: "number",
      flex: 1
    }
  ];

  return (
    <div className={styles.featureTable}>
      <CustomDataGrid
        enableUrlParams
        rows={{
          values: props.tableData.map((m, i) => ({ id: i + 1, ...m })) ?? [],
          count: props.tableData?.length ?? 0
        }}
        columns={columns}
        getRowId={(row) => row.id}
        onRowSelectionModelChange={handleSelection}
        rowSelectionModel={selectionModel}
        paginationOptions={paginationOptions}
        setPaginationOptions={setPaginationOptions}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 }
          }
        }}
        kustoLink={props.kustoLink}
      />
    </div>
  );
}

function navigateTo(tenantId: string) {
  return `/customer?id=${tenantId}`;
}

function copyValue(tenantId: string, setTitle: Dispatch<SetStateAction<string>>) {
  copy(tenantId).then(() => {
    setTitle(COPY_TENANT_ID);
  });
}

function onMouseEnterTenantId(setTitle: Dispatch<SetStateAction<string>>) {
  setTitle(COPY_TENANT_ID);
}
